import type {
  OrderResponse,
  TaskStatus,
  VisitTypeId,
} from '../services/api/types.gen';
import { canAddGoals, canTrackGoalProgress } from '../constants/visitTypes';
import { LOCKED_ORDER_PHYSICIAN_STATUSES } from '../constants/statuses';

/**
 * The set of goal/intervention actions a task's Goals surface may offer.
 * Mirrors `GoalTaskAction`/`task_allows_goal_action` in
 * `backend/app/services/goal_task_gating.py`, the source of truth the
 * backend actually enforces — this is a single frontend mirror so every
 * caller (GoalsTab, DisplayGoalCard, DisplayInterventionCard) reads the same
 * answer instead of re-deriving it from `visit_type`/`order.status` in
 * multiple places.
 */
export interface GoalTaskCapabilities {
  /** Whether a Goals section should render at all for this task. */
  showsGoalsSection: boolean;
  /** Add a new goal (from a template or free text). */
  canAddGoals: boolean;
  /** Add a new intervention to an existing goal. */
  canAddInterventions: boolean;
  /** Record a progress score / note on a goal. */
  canRecordProgress: boolean;
  /** Mark a goal as met or unattainable. */
  canMarkGoalOutcome: boolean;
  /** Record a "perform" event on an intervention. */
  canPerformInterventions: boolean;
  /** Mark an intervention as complete. */
  canCompleteInterventions: boolean;
  /** Push (extend) a goal and its interventions to the next episode. */
  canPushGoals: boolean;
  /** Edit an existing goal (PUT). */
  canEditGoals: boolean;
  /** Delete a goal or one of its interventions. */
  canDeleteGoals: boolean;
  /**
   * Whether this task may act on a goal outside its own discipline (EMR-1663).
   * False for a visit: a goal of a different discipline than the visit
   * renders view-only, collapsed under "Other Discipline Goals". True for an
   * order, which is not discipline-restricted (step 4 of EMR-882 reworks
   * order layout; this flag keeps today's order behavior unchanged).
   */
  canActOnOtherDisciplineGoals: boolean;
}

const NO_CAPABILITIES: GoalTaskCapabilities = {
  showsGoalsSection: false,
  canAddGoals: false,
  canAddInterventions: false,
  canRecordProgress: false,
  canMarkGoalOutcome: false,
  canPerformInterventions: false,
  canCompleteInterventions: false,
  canPushGoals: false,
  canEditGoals: false,
  canDeleteGoals: false,
  canActOnOtherDisciplineGoals: false,
};

/**
 * Capabilities for a visit's Goals tab. Wraps `canAddGoals`/
 * `canTrackGoalProgress` from `constants/visitTypes.ts` exactly as
 * `GoalsTab` already computed them inline — visit behavior is unchanged by
 * this refactor, only relocated so orders can share the same shape.
 *
 * Goal edit/delete are ungated for visits (the backend leaves them open so a
 * goal can be corrected wherever it was created), matching
 * `task_allows_goal_action`'s `MODIFY`/`DELETE` → always `True` for a visit.
 */
export function visitGoalCapabilities(
  visitType: VisitTypeId
): GoalTaskCapabilities {
  const canAdd = canAddGoals(visitType);
  const canProgress = canTrackGoalProgress(visitType);
  return {
    showsGoalsSection: canAdd || canProgress,
    canAddGoals: canAdd,
    canAddInterventions: canAdd,
    canRecordProgress: canProgress,
    canMarkGoalOutcome: canProgress,
    canPerformInterventions: canProgress,
    canCompleteInterventions: canProgress,
    canPushGoals: canProgress,
    canEditGoals: true,
    canDeleteGoals: true,
    canActOnOtherDisciplineGoals: false,
  };
}

/** Order statuses the backend freezes goal writes on for that order. */
export function isOrderGoalLocked(status: TaskStatus): boolean {
  return LOCKED_ORDER_PHYSICIAN_STATUSES.has(status);
}

/**
 * Capabilities for an order's Goals card. Mirrors the backend's
 * `_ORDER_ALLOWED_ACTIONS` allowlist (add goal, complete intervention, push
 * to next episode) intersected with whether the order type supports goals at
 * all (`order.supports_goals`, backed by `GOAL_ENABLED_ORDER_TYPE_VALUES`)
 * and whether the order's current status still allows goal writes
 * (`LOCKED_ORDER_PHYSICIAN_STATUSES`).
 *
 * An order can never record progress, mark a goal met/unattainable, perform
 * an intervention, edit a goal, or delete a goal/intervention — the backend
 * 400s all of those for a task without an ORM-tracked visit type, so the UI
 * never offers them regardless of lock state.
 */
export function orderGoalCapabilities(
  order: Pick<OrderResponse, 'supports_goals' | 'status'>
): GoalTaskCapabilities {
  if (order.supports_goals !== true) {
    return NO_CAPABILITIES;
  }
  const writesEnabled = !isOrderGoalLocked(order.status);
  return {
    ...NO_CAPABILITIES,
    showsGoalsSection: true,
    canAddGoals: writesEnabled,
    canAddInterventions: writesEnabled,
    canCompleteInterventions: writesEnabled,
    canPushGoals: writesEnabled,
    canActOnOtherDisciplineGoals: true,
  };
}
